/**
 * Les journées, vues par l'écran — tout ce qui se calcule hors de React.
 *
 * L'API ne rend QUE DES FAITS : combien de camions, combien de pointés, la
 * veille a-t-elle un lieu de fin. L'état — « à pointer », « bloqué »,
 * « incomplète » — se déduit ici, dans des fonctions pures que leurs tests
 * éprouvent en une milliseconde.
 *
 * POURQUOI PAS EN SQL : un état calculé dans la base ne se teste qu'avec une
 * base, et se corrige avec une migration. C'est déjà le partage retenu pour le
 * poste client et pour les fréquences de facturation.
 */

import type { LigneDeChaine as LigneDeChaineCore } from '@stafa/core';
import type { PartDeCa } from './api.js';

/** Un jour de la file, tel que `GET /journees` le rend. */
export interface JourListe {
  readonly jour: string;
  readonly ordres: number;
  readonly camions: number;
  readonly pointes: number;
  readonly clotures: number;
  readonly etapes_sans_camion: number;
  readonly km_reels: string | null;
  readonly km_estimes: string | null;
  readonly peage: string | null;
}

/** Un camion d'un jour, tel que `GET /journees/:jour` le rend. */
export interface CamionDuJour {
  readonly vehicule_id: string | null;
  readonly immatriculation: string | null;
  readonly chauffeurs: readonly string[];
  readonly remorques: readonly string[];
  readonly etapes: number;
  readonly depart_nom: string | null;
  readonly depart_repris_du: string | null;
  readonly veille_sans_fin: boolean;
  readonly fin_nom: string | null;
  readonly km_reels: string | null;
  readonly km_estimes: string | null;
  readonly peage: string | null;
  readonly segments: number;
  readonly segments_sans_km: number;
  readonly pointee_le: string | null;
  readonly cloturee_le: string | null;
}

export type EtatJour = 'cloturee' | 'tout_pointe' | 'en_cours' | 'a_pointer';

export type EtatCamion =
  | 'sans_affectation'
  | 'cloture'
  | 'bloque'
  | 'pointe'
  | 'incomplete'
  | 'a_pointer';

/**
 * L'état d'un JOUR.
 *
 * L'ORDRE DES TESTS EST LA RÈGLE, et il se lit de haut en bas : une journée
 * dont tous les camions sont clôturés est close, quoi qu'il arrive ensuite.
 *
 * `camions` À ZÉRO N'EXISTE PAS dans la liste — un jour y figure parce qu'il
 * porte une étape. Mais si toutes ses étapes sont sans camion, le compte tombe
 * à zéro, et « 0 sur 0 clôturés » ne doit PAS se lire « tout est fait ». D'où
 * la garde : sans camion, il reste à pointer.
 */
export function etatDuJour(j: JourListe): EtatJour {
  if (j.camions === 0) return 'a_pointer';
  if (j.clotures >= j.camions) return 'cloturee';
  if (j.pointes >= j.camions) return 'tout_pointe';
  if (j.pointes === 0) return 'a_pointer';
  return 'en_cours';
}

/**
 * L'état d'un CAMION dans sa journée.
 *
 * BLOQUÉ PASSE AVANT POINTÉ, et ce n'est pas un détail : un camion dont la
 * veille ne dit pas où il s'est arrêté n'a pas de point de départ, donc ses
 * kilomètres sont faux. Le dire « pointé » serait afficher un chiffre juste en
 * apparence. Seule la clôture passe avant — une journée close ne se rouvre pas
 * parce qu'on a cassé la veille après coup.
 */
export function etatDuCamion(c: CamionDuJour): EtatCamion {
  if (c.vehicule_id === null) return 'sans_affectation';
  if (c.cloturee_le !== null) return 'cloture';
  if (c.veille_sans_fin) return 'bloque';
  if (c.pointee_le !== null) return 'pointe';
  // Des segments existent mais il manque des kilomètres ou le lieu de fin :
  // le travail est commencé, pas fini. L'écran dira LEQUEL manque.
  if (c.segments > 0 && (c.segments_sans_km > 0 || c.fin_nom === null)) {
    return 'incomplete';
  }
  return 'a_pointer';
}

/** Ce qui manque à un camion incomplet, en français, ou `null`. */
export function ceQuiManque(c: CamionDuJour): string | null {
  const manques: string[] = [];
  if (c.segments_sans_km > 0) {
    manques.push(
      c.segments_sans_km === 1
        ? 'les kilomètres d’un trajet'
        : `les kilomètres de ${String(c.segments_sans_km)} trajets`,
    );
  }
  if (c.fin_nom === null) manques.push('le lieu de fin');
  if (manques.length === 0) return null;
  return manques.join(' et ');
}

/** Le mot affiché, et la classe de statut qui va avec — toutes deux existantes. */
export function motDuJour(e: EtatJour): { texte: string; classe: string } {
  switch (e) {
    case 'cloturee':
      return { texte: 'Clôturée', classe: 'affrete' };
    case 'tout_pointe':
      return { texte: 'Tout pointé', classe: 'livre' };
    case 'en_cours':
      return { texte: 'En cours', classe: 'planifie' };
    case 'a_pointer':
      return { texte: 'À pointer', classe: 'attente' };
  }
}

export function motDuCamion(e: EtatCamion): { texte: string; classe: string } {
  switch (e) {
    case 'sans_affectation':
      return { texte: 'Sans affectation', classe: 'litige' };
    case 'cloture':
      return { texte: 'Clôturé', classe: 'affrete' };
    case 'bloque':
      return { texte: 'Bloqué', classe: 'litige' };
    case 'pointe':
      return { texte: 'Pointé', classe: 'livre' };
    case 'incomplete':
      return { texte: 'Incomplet', classe: 'attente' };
    case 'a_pointer':
      return { texte: 'À pointer', classe: 'attente' };
  }
}

/** Combien de camions restent à pointer ce jour-là. Jamais négatif. */
export function restantAPointer(j: JourListe): number {
  return Math.max(j.camions - j.pointes, 0);
}

/**
 * L'écart entre les kilomètres relevés et estimés.
 *
 * `null` DÈS QU'UN DES DEUX MANQUE, et jamais zéro : « pas d'écart » et « pas
 * de relevé » sont deux choses différentes, et les confondre ferait passer une
 * journée non pointée pour une journée parfaitement estimée.
 *
 * AUCUN SEUIL N'EST APPLIQUÉ ICI — tranché le 14/09 : l'écart s'affiche
 * toujours, rien ne le met en avant. C'est l'exploitation qui juge.
 */
export function ecartKilometrique(
  reels: string | null,
  estimes: string | null,
): number | null {
  if (reels === null || estimes === null) return null;
  const r = Number(reels);
  const e = Number(estimes);
  if (!Number.isFinite(r) || !Number.isFinite(e)) return null;
  return r - e;
}

/** Un écart signé, pour l'affichage : « + 74 », « − 4 », « 0 ». */
export function ecartDit(ecart: number | null): string {
  if (ecart === null) return '—';
  if (ecart === 0) return '0';
  return ecart > 0 ? `+ ${String(ecart)}` : `− ${String(Math.abs(ecart))}`;
}

/**
 * Le jour en toutes lettres : « lundi 7 septembre ».
 *
 * `Intl` PLUTÔT QU'UNE TABLE DE MOIS ÉCRITE À LA MAIN, et la date est
 * construite en UTC : `new Date('2026-09-07')` est déjà à minuit UTC, mais
 * l'afficher dans un fuseau à l'ouest donnerait la veille.
 */
export function jourDit(jour: string): string {
  const [a, m, j] = jour.split('-').map(Number);
  if (a === undefined || m === undefined || j === undefined) return jour;
  const d = new Date(Date.UTC(a, m - 1, j));
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(d);
}

/** « CD-220-XY puis EF-660-PL » — un échange de remorque ne coupe pas la journée. */
export function suiteDit(valeurs: readonly string[]): string {
  if (valeurs.length === 0) return '—';
  if (valeurs.length === 1) return valeurs[0]!;
  return `${valeurs.slice(0, -1).join(', ')} puis ${valeurs[valeurs.length - 1]!}`;
}

// ===========================================================================
// LA JOURNÉE D'UN CAMION — déménagée dans `@stafa/core` le 30/10/2026
// ===========================================================================
// Tout ce qui suit vivait ICI. Le serveur en a besoin pour chiffrer la marge
// d'une journée, et deux exemplaires du même compte de kilomètres finiraient
// par ne plus dire la même chose. Voir `packages/core/src/journee_camion.ts`,
// qui porte le pourquoi en entier.
//
// LES RÉEXPORTS RESTENT pour que les écrans n'aient rien à changer : un
// déménagement et une correction dans la même modification, on ne saurait plus
// lequel des deux a cassé quoi.

/**
 * LE DOUBLE BOUTON « À VIDE / CHARGÉ » DU POINTAGE — lot 135, 02/11/2026.
 *
 * Ce qu'il faut ENVOYER quand on appuie sur un des deux boutons :
 *
 *   - `undefined` : rien, le bouton appuyé est déjà le bon ;
 *   - `null`      : on revient à la déduction — le bouton choisi est celui que
 *                   Stafa déduit, le forçage s'efface (c'était la troisième
 *                   option de l'ancien sélecteur, sans laquelle un forçage
 *                   fautif resterait pour toujours) ;
 *   - `true` / `false` : on force contre la déduction.
 */
export function forcageChoisi(
  trajet: Pick<LigneDeChaineCore, 'nature' | 'en_charge'>,
  charge: boolean,
): boolean | null | undefined {
  const deduit = trajet.nature === 'parcours_charge';
  const effectif = trajet.en_charge ?? deduit;
  if (charge === effectif) return undefined;
  return charge === deduit ? null : charge;
}

/**
 * DÉPLACER UN ARRÊT D'UN CRAN DANS LA JOURNÉE — lot 139, 02/11/2026.
 *
 * Constaté par Mohamed au premier vrai pointage : un arrêt s'affichait au
 * mauvais rang. Sans rang posé à la main, la journée suit l'heure d'arrivée
 * validée, puis le créneau, puis le rang dans l'ordre (`GET
 * /journees/:jour/:vehicule`), et ce n'est pas toujours l'ordre roulé.
 *
 * Rend la LISTE COMPLÈTE dans le nouvel ordre — c'est ce que la route
 * `PUT /journees/:jour/:vehicule/etapes` exige — ou `null` quand le geste ne
 * change rien (premier arrêt qu'on monte, dernier qu'on descend, arrêt
 * inconnu).
 */
export function deplacerArret(
  ordre: readonly string[],
  etapeId: string,
  sens: -1 | 1,
): string[] | null {
  const i = ordre.indexOf(etapeId);
  const j = i + sens;
  if (i < 0 || j < 0 || j >= ordre.length) return null;
  const suite = [...ordre];
  suite[i] = ordre[j]!;
  suite[j] = etapeId;
  return suite;
}

/**
 * COMMENT UNE PART DU CHIFFRE D'AFFAIRES EST CALCULÉE, en toutes lettres —
 * lot 143, 02/11/2026. Demande de Mohamed : « au clic voir les OT et le
 * partage ». Les montants arrivent en CENTIMES ; `euros` les met en forme.
 */
export function explicationDePart(
  p: Pick<
    PartDeCa,
    'regle' | 'ca_ordre_centimes' | 'km_jour' | 'base_km' | 'deja_reparti_centimes'
  >,
  euros: (centimes: number) => string,
): string {
  const km = (n: number): string => `${new Intl.NumberFormat('fr-FR').format(n)} km`;
  switch (p.regle) {
    case 'seul':
      return `${euros(p.ca_ordre_centimes)} € — transport fait en entier ce jour-là`;
    case 'roule':
      return `${euros(p.ca_ordre_centimes)} € × ${km(p.km_jour)} ÷ ${km(p.base_km ?? 0)} chargés sur tout le transport`;
    case 'distance':
      return `${euros(p.ca_ordre_centimes)} € × ${km(p.km_jour)} ÷ ${km(p.base_km ?? 0)} (distance de l’OT)`;
    case 'reste':
      return `${euros(p.ca_ordre_centimes)} € − ${euros(p.deja_reparti_centimes ?? 0)} € déjà comptés les autres jours`;
  }
}

export {
  carburantDeLaJournee,
  chaineDeLaJournee,
  dureeDite,
  enChargeEffectif,
  natureDite,
  supportDuJour,
  totauxDeLaJournee,
} from '@stafa/core';
export type {
  ArretDeLaJournee,
  Carburant,
  Chaine,
  EnTeteJournee,
  LigneArret,
  LigneDeChaine,
  LigneJournee,
  NatureTrajet,
  OrdreDuJour,
  SegmentLu,
  TotauxJournee,
} from '@stafa/core';
